import { useEffect, useState } from 'react';
import { HttpService } from '@/features/api/http.service';
import { isImportJobTerminal } from '@/features/zones/utils/isImportJobTerminal';
import type { IImportJobSnapshot, ImportJobStatus, ISyncCounts } from '@/types/api';

export interface ImportJobStreamState {
  status: ImportJobStatus | null;
  counts: ISyncCounts | null;
  /** True once the job reaches a terminal status (`completed` / `failed`). */
  isTerminal: boolean;
  /** Set only when both the SSE stream and the JSON snapshot fallback fail. */
  error: Error | null;
}

/** Payload of each SSE `status` frame (status + running counts). */
interface StatusFrame {
  status: ImportJobStatus;
  counts: ISyncCounts;
}

const INITIAL_STATE: ImportJobStreamState = {
  status: null,
  counts: null,
  isTerminal: false,
  error: null,
};

/**
 * Subscribe to a Notion sync job's live progress over Server-Sent Events
 * (research Decision 11). Opens an `EventSource` against
 * `GET /zones/imports/jobs/{jobId}` (the browser sends
 * `Accept: text/event-stream`, which the backend content-negotiates into
 * the SSE stream) and updates on each `event: status` frame, closing on a
 * terminal status or the trailing `event: end`.
 *
 * If the stream errors (proxy drop, non-streaming client, auth quirk), it
 * degrades gracefully to a one-shot JSON snapshot via `HttpService` — the
 * SSE path is the optimisation, the snapshot is the correctness guarantee.
 * Callers render progress from the returned state and fetch the terminal
 * per-row outcome separately once `isTerminal` is true.
 */
export function useImportJobStream(jobId: string): ImportJobStreamState {
  const [state, setState] = useState<ImportJobStreamState>(INITIAL_STATE);

  useEffect(() => {
    if (!jobId) return;

    setState(INITIAL_STATE);
    // `disposed` flips only on unmount; closing the EventSource (terminal
    // status / `end` / error) does NOT set it, so the snapshot fallback can
    // still run after the stream is torn down.
    let disposed = false;
    // EventSource auto-reconnects and fires `onerror` on every failed
    // attempt; guard so a flapping connection only triggers one snapshot
    // fetch rather than a fan-out of concurrent requests.
    let fallbackInFlight = false;
    let source: EventSource | null = null;

    const closeSource = () => {
      source?.close();
      source = null;
    };

    const applyFrame = (frame: StatusFrame) => {
      if (disposed) return;
      const terminal = isImportJobTerminal(frame.status);
      setState((prev) => ({
        ...prev,
        status: frame.status,
        counts: frame.counts,
        isTerminal: terminal,
      }));
      if (terminal) closeSource();
    };

    const fallbackToSnapshot = async () => {
      if (fallbackInFlight) return;
      fallbackInFlight = true;
      try {
        const snap = await HttpService.get<string, IImportJobSnapshot>(
          `/zones/imports/jobs/${jobId}`,
        );
        if (disposed) return;
        setState({
          status: snap.status,
          counts: snap.counts ?? null,
          isTerminal: isImportJobTerminal(snap.status),
          error: null,
        });
      } catch (err) {
        if (disposed) return;
        setState((prev) => ({
          ...prev,
          error: err instanceof Error ? err : new Error(String(err)),
        }));
      }
    };

    source = new EventSource(HttpService.buildApiUrl(`/zones/imports/jobs/${jobId}`), {
      withCredentials: true,
    });

    source.addEventListener('status', (event) => {
      try {
        applyFrame(JSON.parse((event as MessageEvent).data) as StatusFrame);
      } catch {
        // Ignore malformed frames — the backend owns the wire shape.
      }
    });

    source.addEventListener('end', () => {
      closeSource();
    });

    source.onerror = () => {
      // EventSource auto-reconnects, but a 4xx/5xx or a closed stream lands
      // here. Close it and fall back to a single JSON snapshot so the UI
      // still converges to the current state.
      closeSource();
      void fallbackToSnapshot();
    };

    return () => {
      disposed = true;
      closeSource();
    };
  }, [jobId]);

  return state;
}
